"use client";

import React, {
  createContext,
  useContext,
  ReactNode,
  useState,
  useMemo,
  useEffect,
} from "react";
import {
  darkThemeConfigColors,
  lightThemeConfigColors,
  TypeThemeConfigColors,
} from "@/theme/theme-config";
import { ThemeConfig } from "antd";
interface ThemeContextType {
  themeConfig: ThemeConfig;
  themeConfigColors: TypeThemeConfigColors;
  mode: Mode;
  setMode: React.Dispatch<React.SetStateAction<Mode>>;
}

type Mode = "dark" | "light";

const ThemeContext = createContext<ThemeContextType>({
  themeConfig: {},
  themeConfigColors: lightThemeConfigColors,
  mode: "light",
  setMode: () => {},
});

const getInitialTheme = (): Mode => {
  if (typeof window !== "undefined") {
    return localStorage.getItem("theme") === "dark" ? "dark" : "light";
  }
  return "light";
};

export const ThemeProvider = ({ children }: { children: ReactNode }) => {
  const [mode, setMode] = useState<Mode>(getInitialTheme());
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    document.body.classList.toggle("dark-mode", mode === "dark");
    document.body.classList.toggle("dark", mode === "dark");
  }, []);

  useEffect(() => {
    if (mounted) {
      document.body.classList.toggle("dark-mode", mode === "dark");
      document.body.classList.toggle("dark", mode === "dark");
    }
  }, [mode, mounted]);

 const dynamicThemeConfig: ThemeConfig = useMemo(() => {
  const colors =
      mode === "dark" ? darkThemeConfigColors : lightThemeConfigColors;
  return {
    token: {
       fontSize: 16,
        colorPrimary: colors.primary,
        colorBorder: colors.borderColor,
        colorError: colors.error,
        colorText: colors.primaryText,
        colorTextDisabled: colors.textDisabled,
        colorTextSecondary: colors.secondaryText,
        colorSuccess: colors.success,
        fontFamily: "var(--font-inter-tight), sans-serif",
        controlHeight: 41,
        colorBgContainer: colors.cardBackgroundColor,
        colorBorderSecondary: colors.secondaryBorderColor,
        colorBgElevated: colors.secondaryBackground,
        colorTextDescription: colors.secondaryText,
        colorFillSecondary: colors.background,
        colorTextQuaternary: colors.quaternaryText,
        colorErrorBg: colors.bgError,
        colorErrorBorder: colors.borderError,
        colorInfo: colors.colorInfo,
        colorWarning: colors.colorWarning,
        colorWarningBg: colors.colorWarningBg,
        colorWarningBorder: colors.colorWarningBorder,
        colorInfoBg: colors.colorInfoBg,
        colorInfoBorder: colors.colorInfoBorder,
        colorSuccessBg: colors.colorSuccessBg,
        colorSuccessBorder: colors.colorSuccessBorder,
        colorIcon: colors.primaryText,
    },
    components: {
      Tabs: {
        itemActiveColor: 'var(--primaryText)',
        verticalItemMargin: '0',
        cardPadding: '8px 16px',
      },
      Form: {
        verticalLabelPadding: '0 0 4px',
        controlHeight: 41,
        labelFontSize: 14,
        labelRequiredMarkColor: 'var(--error)',
      },
      Input: {
        colorTextPlaceholder: 'var(--secondaryText)',
        colorTextDisabled: 'var(--textDisabled)',
        borderRadius: 8,
        colorError: 'var(--error)',
        colorErrorBorderHover: 'var(--error)',
        activeBorderColor: 'var(--primary)',
        activeShadow: '0 0 0 2px var(--primaryLight)',
        hoverBorderColor: 'var(--primary)',
        colorBorder: 'var(--borderColor)',
        lineWidth:2,
        fontSize:14,
        height:48
      },
      InputNumber: {
        colorBorder: 'var(--borderColor)',
        activeBorderColor: 'var(--primary)',
        activeShadow: '0 0 0 2px var(--primaryLight)',
        colorTextPlaceholder: 'var(--secondaryText)',
        hoverBorderColor: 'var(--primary)',
        colorTextDisabled: 'var(--textDisabled)',
        borderRadius: 8,
        colorError: 'var(--error)',
        colorErrorBorderHover: 'var(--error)',
        lineWidth:2,
        fontSize:14,
        height:48
      },
      Card: {
        colorTextHeading: 'var(--primaryText)',
        colorBgContainer: 'var(--cardBackgroundColor)', // or --cardBackgroundColor? Confirm intent
        colorBorder: 'var(--borderColor)',
      },
      Typography: {
        colorText: 'var(--primaryText)',
        colorTextSecondary: 'var(--secondaryText)',
      },
      Layout: {
        siderBg: 'var(--siderBg)',
        bodyBg: 'var(--layoutBackground)',
      },
      Checkbox: {
        colorBorder: 'var(--secondaryText)',
        colorBgContainer: 'var(--transparent)',
        lineWidth: 2,
        paddingXS: 15,
        controlInteractiveSize: 20,
      },
      Select: {
        optionSelectedColor: 'var(--primaryText)',
        optionSelectedBg: 'var(--primaryLight)',
        colorTextSecondary: 'var(--secondary)',
        colorBorder: 'var(--borderColor)',
        activeBorderColor: 'var(--primary)',
        hoverBorderColor: 'var(--primary)',
        activeShadow: '0 0 0 2px var(--primaryLight)',
        colorTextPlaceholder: 'var(--secondaryText)',
        borderRadius:8,
        lineWidth:2,
        fontSize:14,
        height:48
      },
      Button: {
        borderRadius: 8,
        borderRadiusSM: 8,
        borderRadiusLG: 8,
        borderRadiusXS: 8,
        colorBorder: 'var(--primary)',
        colorText: 'var(--primaryText)',
        padding: 12,
        paddingInline: '16px',
        paddingBlock: '8px',
        controlHeight: 37,
        primaryShadow: 'var(--primaryButtonShadow)',
        colorPrimaryBg: 'var(--gradient-primary)',
        defaultBorderColor: mode == "dark" ? "#333333" : "#ffffff",
        defaultHoverBg:
            mode == "dark"
              ? "linear-gradient(154.92deg, #444444 15.82%, #555555 83.93%)"
              : "linear-gradient(154.92deg, #F5F5F5 15.82%, #EDEAF0 83.93%)",
        defaultHoverBorderColor: mode == "dark" ? "#444444" : "#ffffff",
        defaultShadow: 'var(--shadow-md)',
        defaultBg:
            mode == "dark"
              ? "linear-gradient(154.92deg, #444444 15.82%, #555555 83.93%)"
              : "linear-gradient(154.92deg, #F5F5F5 15.82%, #EDEAF0 83.93%)",
        textHoverBg: 'var(--backgroundAccentColor)',
      },
      Segmented: {
        itemSelectedBg: 'var(--primaryLight)',
        trackBg: 'var(--cardBackgroundColor)',
        borderRadiusSM: 8,
        borderRadiusXS: 6,
        trackPadding: 2,
        itemSelectedColor: 'var(--primaryText)',
      },
      Divider: {
        fontSize: 16,
        colorBorder: 'var(--grey-20)', // #CECECE → --grey-20
        colorBorderBg: 'var(--grey-20)',
        margin: 24,
      },
      Menu: {
        itemBorderRadius: 8,
        itemPaddingInline: '8px',
        padding: 16,
        fontSize:14,
        fontWeightStrong: 600,
        fontWeight:600,
        darkItemColor: 'var(--secondaryText)',
        darkItemHoverColor: 'var(--secondaryText)',
        darkItemSelectedColor: 'var(--primaryText)',
        darkItemHoverBg: 'var(--secondaryBorderColor)',
        darkItemSelectedBg: 'var(--menuItemSelectedBG)',
        darkSubMenuItemBg:'transparent',
      
      },
      Collapse: {
        contentBg: 'var(--cardBackgroundColor)',
        headerBg: 'var(--cardBackgroundColor)',
        contentPadding: '8px',
      },
      Table: {
        headerBg: 'var(--background)',
        rowHoverBg: 'var(--cardBackgroundColor)',
        footerBg: 'var(--cardBackgroundColor)',
        colorBgContainer: 'var(--transparent)',
      },
      Pagination: {
        itemActiveBg: 'var(--menuItemSelectedBG)',
        colorBorder: 'var(--transparent)',
      },
      Modal: {
        contentBg: 'var(--layoutBackground)',
        footerBg: 'var(--layoutBackground)',
        headerBg: 'var(--layoutBackground)',
      },
      Alert: {
        colorPrimary: 'var(--black)', // or consider --primaryText
      },
    },
  };
}, [mode]);

  const themeConfigColors =
    mode === "dark" ? darkThemeConfigColors : lightThemeConfigColors;

  if (!mounted) {
    return null;
  }
  return (
    <ThemeContext.Provider
      value={{
        themeConfig: dynamicThemeConfig,
        themeConfigColors,
        mode,
        setMode,
      }}
    >
      {children}
    </ThemeContext.Provider>
  );
};

export const useTheme = () => useContext(ThemeContext);
